import { useCallback, useMemo } from 'react';
import { clamp } from 'lodash';
import { useSelector } from 'react-redux';
import { Flex } from '@cdl/heartbeat-react';
import { useRuler2dDimensions } from '../hooks/useRuler2dDimensions';
import { useRuler2dMetrics } from '../hooks/useRuler2dMetrics';
import { useStrip } from '../../plot/hooks/useStrip';
import { squareToPixel } from '../../plot/utils/graph.utils';
import { roundedDurationSelector } from '../../ecg/selectors/ecg.selectors';

const INFO_WIDTH = squareToPixel(9);
const INFO_HEIGHT = squareToPixel(4 / 3);
const INFO_MARGIN = squareToPixel(0.5);

export const Ruler2dInfo: React.FC = () => {
  const {
    x,
    y,
    height: ruler2dHeight,
    width: ruler2dWidth,
  } = useRuler2dDimensions();
  const { xScale, height: stripHeight, width: stripWidth } = useStrip();
  const ecgDuration = useSelector(roundedDurationSelector);
  const ruler2dInfo = useRuler2dMetrics();

  const clampX = useCallback(
    (x: number) => {
      return clamp(x, 0, xScale(ecgDuration) - INFO_WIDTH);
    },
    [ecgDuration, xScale],
  );

  const topPlacementYPosition = y - INFO_HEIGHT - INFO_MARGIN;
  const bottomPlacementYPosition = y + ruler2dHeight + INFO_MARGIN;
  const rightPlacementXPosition = x + ruler2dWidth + INFO_MARGIN;
  const leftPlacementXPosition = x - INFO_WIDTH - INFO_MARGIN;

  const isNoSpaceOnTop = topPlacementYPosition < 0;
  const isNoSpaceOnBottom =
    bottomPlacementYPosition + INFO_HEIGHT > stripHeight;
  const isNoSpaceOnTopAndBottom = isNoSpaceOnTop && isNoSpaceOnBottom;
  const isNoSpaceOnRight = rightPlacementXPosition + INFO_WIDTH > stripWidth;

  // info X position follows the ruler X axis moves
  // info never goes out of the strip
  // if no space left on top and bottom of the ruler
  // place info on the right if there is space otherwise on the left
  const xPosition = useMemo(() => {
    if (isNoSpaceOnTopAndBottom) {
      if (isNoSpaceOnRight) {
        return clampX(leftPlacementXPosition);
      } else {
        return clampX(rightPlacementXPosition);
      }
    }

    return clampX(x);
  }, [
    x,
    isNoSpaceOnRight,
    isNoSpaceOnTopAndBottom,
    rightPlacementXPosition,
    leftPlacementXPosition,
    clampX,
  ]);

  // info Y position follows the ruler Y axis moves
  // default postition is on top
  // if no space left on top, place info on the bottom
  // if no space left on top and bottom, place info on the center of the Y axis of the ruler
  const yPosition = useMemo(() => {
    if (isNoSpaceOnTop) {
      if (isNoSpaceOnBottom) {
        return stripHeight / 2 - INFO_HEIGHT / 2;
      } else {
        return bottomPlacementYPosition;
      }
    }

    return topPlacementYPosition;
  }, [
    isNoSpaceOnTop,
    topPlacementYPosition,
    stripHeight,
    isNoSpaceOnBottom,
    bottomPlacementYPosition,
  ]);

  if (ruler2dHeight === 0 || ruler2dWidth === 0) {
    return <></>;
  }

  return (
    <foreignObject
      fontSize='1.125rem'
      x={xPosition}
      y={yPosition}
      width={INFO_WIDTH}
      height={INFO_HEIGHT}
    >
      <Flex
        bgColor='neutral.300'
        borderRadius='4px'
        color='white'
        justifyContent='center'
        alignItems='center'
        opacity={0.9}
        userSelect='none'
        data-testid='ruler2d-info'
        fontSize='l'
        height='2.5rem'
      >
        {ruler2dInfo}
      </Flex>
    </foreignObject>
  );
};
